// ============================================
// WebSocket 数据源适配器
// 使用浏览器原生 WebSocket
// ============================================

import type { DatasourceAdapter } from '../../types/databinding';
import type { DatasourceConfig, WebSocketConfig } from '../../types/designer';
import { EventEmitter } from '../EventEmitter';

export class WebSocketAdapter extends EventEmitter implements DatasourceAdapter {
  readonly type = 'websocket' as const;
  private status: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 3000;
  private config: DatasourceConfig | null = null;
  private subscriptions: Map<string, Set<(data: any) => void>> = new Map();
  /** 2026-09-14 修复（FE-56）：用户主动断开标记（区别于连接失败/断线，手动断开不重连） */
  private manualClose = false;

  async connect(config: DatasourceConfig): Promise<void> {
    if (config.type !== 'websocket') throw new Error('Invalid config type');
    this.config = config;
    this.manualClose = false;
    this.status = 'connecting';
    this.emit('status:change', this.status);

    const wsConfig = config.config as WebSocketConfig;

    return new Promise((resolve, reject) => {
      try {
        const protocol = wsConfig.protocol ? `wss://${wsConfig.url}` : `ws://${wsConfig.url}`;
        this.ws = new WebSocket(wsConfig.url.startsWith('ws') ? wsConfig.url : protocol);

        // 2026-09-14 修复（FE-26）：连接超时——8 秒未 open 则报「WebSocket 连接超时」
        // （原实现无超时：不可达/黑洞地址会一直悬挂在 connecting，测试预期约 8 秒超时错误）
        const connectTimeout = setTimeout(() => {
          if (this.status === 'connecting') {
            this.status = 'error';
            this.emit('status:change', this.status);
            this.emit('error', 'WebSocket 连接超时');
            try { this.ws?.close(); } catch { /* noop */ }
            reject(new Error('WebSocket 连接超时'));
          }
        }, 8000);

        this.ws.onopen = () => {
          clearTimeout(connectTimeout);
          this.status = 'connected';
          this.reconnectAttempts = 0;
          this.emit('status:change', this.status);
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch {
            this.handleMessage(event.data);
          }
        };

        this.ws.onerror = (event) => {
          this.status = 'error';
          this.emit('status:change', this.status);
          this.emit('error', 'WebSocket error');
          reject(new Error('WebSocket connection failed'));
        };

        this.ws.onclose = () => {
          // 2026-09-14 修复（FE-56）：浏览器事件序固定为 error→close，原实现 onclose 无条件置 disconnected
          // 把 error 覆盖掉（无服务时状态机显示 connecting→disconnected）。失败后保持 error，不自动重连覆盖状态。
          if (!this.manualClose && this.status === 'error') {
            this.emit('status:change', this.status);
            return;
          }
          this.status = 'disconnected';
          this.emit('status:change', this.status);
          if (!this.manualClose) this.attemptReconnect();
        };
      } catch (err: any) {
        this.status = 'error';
        this.emit('status:change', this.status);
        reject(err);
      }
    });
  }

  disconnect(): void {
    // 2026-09-14 修复（FE-56）：标记主动断开——避免 onclose 触发自动重连 / 状态被判为失败
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.subscriptions.clear();
    this.status = 'disconnected';
    this.config = null;
    this.emit('status:change', this.status);
  }

  subscribe(topic: string, callback: (data: any) => void): void {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic)!.add(callback);

    // 发送订阅消息
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'subscribe', topic }));
    }
    this.emit('subscribe', { topic });
  }

  unsubscribe(topic: string): void {
    this.subscriptions.delete(topic);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'unsubscribe', topic }));
    }
    this.emit('unsubscribe', { topic });
  }

  send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }

  getStatus() {
    return this.status;
  }

  private handleMessage(data: any): void {
    // 尝试根据 topic 分发
    const topic = data.topic || data.channel || 'default';
    const callbacks = this.subscriptions.get(topic);
    if (callbacks) {
      callbacks.forEach((cb) => cb(data));
    }

    // 也分发给通配订阅
    const wildcardCallbacks = this.subscriptions.get('*');
    if (wildcardCallbacks) {
      wildcardCallbacks.forEach((cb) => cb(data));
    }

    this.emit('message', data);
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('error', 'Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * Math.min(this.reconnectAttempts, 5);

    this.reconnectTimer = setTimeout(() => {
      if (this.config) {
        this.connect(this.config).catch(() => {});
      }
    }, delay);
  }
}
